import { zodResolver } from '@hookform/resolvers/zod';
import {
  JobApplicationInputSchema,
  type JobApplicationInput,
  type JobPosting,
} from '@resolute/schema';
import { Button, Checkbox, Input, Select } from '@resolute/ui';
import { motion, useReducedMotion } from 'framer-motion';
import { CheckCircle2, Send } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';


import { useSubmitApplication } from '../api';

import { FormShell } from '@/components/form-shell';
import { PhoneField } from '@/components/phone-field';
import { RequestError } from '@/lib/api-client';
import { EASE_OUT_EXPO } from '@/lib/motion';


const NOTE_MAX = 1500;
const GENERAL_APPLICATION = '';

/** `jobId` is driven by the position dropdown outside react-hook-form/zod
 *  validation (it's always a valid selection from a fixed option list, so
 *  there's nothing to validate) — combined into the payload at submit time. */
const FormFieldsSchema = JobApplicationInputSchema.omit({ jobId: true });
type ApplicationFormValues = Omit<JobApplicationInput, 'jobId'>;

export interface ApplicationFormProps {
  jobs: JobPosting[];
  /** Preselect a position (e.g. after tapping "Apply now" on a job card). */
  selectedJobId?: string;
}

export function ApplicationForm({ jobs, selectedJobId }: ApplicationFormProps) {
  const reduce = useReducedMotion();
  const mutation = useSubmitApplication();
  const [jobId, setJobId] = useState(selectedJobId ?? GENERAL_APPLICATION);

  useEffect(() => {
    if (selectedJobId) setJobId(selectedJobId);
  }, [selectedJobId]);

  const positionOptions = useMemo(
    () => [
      { value: GENERAL_APPLICATION, label: 'General application (no specific role)' },
      ...jobs.map((job) => ({ value: job.id, label: job.title })),
    ],
    [jobs],
  );

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ApplicationFormValues>({
    resolver: zodResolver(FormFieldsSchema),
    mode: 'onTouched',
    reValidateMode: 'onChange',
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      currentCompany: '',
      experienceYears: 0,
      resumeUrl: '',
      linkedinUrl: '',
      coverNote: '',
      consent: false as unknown as true,
      website: '',
    },
  });

  const noteValue = watch('coverNote') ?? '';
  const noteCount = noteValue.length;

  const onSubmit = handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync({ ...values, jobId: jobId || undefined });
      reset();
      setJobId(GENERAL_APPLICATION);
    } catch {
      // Surfaced below via mutation.isError; keeps values so the user can retry.
    }
  });

  const errorMessage = useMemo(() => {
    if (!mutation.isError) return null;
    return mutation.error instanceof RequestError
      ? mutation.error.message
      : 'Something went wrong submitting your application. Please try again.';
  }, [mutation.isError, mutation.error]);

  if (mutation.isSuccess) {
    return <ApplicationSuccess reduce={reduce ?? false} onReset={() => mutation.reset()} />;
  }

  return (
    <FormShell
      onSubmit={onSubmit}
      ariaLabel="Careers application form"
      honeypot={register('website')}
      error={errorMessage}
      reduce={reduce ?? false}
      footer={
        <Button
          type="submit"
          size="lg"
          loading={isSubmitting || mutation.isPending}
          className="w-full sm:w-auto sm:self-start"
        >
          {!(isSubmitting || mutation.isPending) && <Send className="size-4" aria-hidden="true" />}
          Submit Application
        </Button>
      }
    >

      <Select
        label="Position applying for"
        options={positionOptions}
        value={jobId}
        onChange={(e) => setJobId(e.target.value)}
      />

      <div className="grid gap-5 sm:grid-cols-2">
        <Input
          label="Full name"
          autoComplete="name"
          placeholder="Your full name"
          required
          error={errors.fullName?.message}
          {...register('fullName')}
        />
        <Input
          label="Email address"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          required
          error={errors.email?.message}
          {...register('email')}
        />
        <PhoneField
          control={control}
          name="phone"
          label="Phone number"
          required
          error={errors.phone?.message}
        />
        <Input
          label="Current company"
          autoComplete="organization"
          placeholder="Company name"
          hint="Optional"
          error={errors.currentCompany?.message}
          {...register('currentCompany')}
        />
        <Input
          label="Years of experience"
          type="number"
          inputMode="numeric"
          min={0}
          max={50}
          required
          error={errors.experienceYears?.message}
          {...register('experienceYears')}
        />
        <Input
          label="LinkedIn profile"
          type="url"
          autoComplete="url"
          placeholder="https://linkedin.com/in/you"
          hint="Optional"
          error={errors.linkedinUrl?.message}
          {...register('linkedinUrl')}
        />
      </div>

      <Input
        label="Resume link"
        type="url"
        placeholder="Google Drive, Dropbox or any public link to your resume"
        required
        hint="Paste a shareable link — direct file upload isn't supported yet"
        error={errors.resumeUrl?.message}
        {...register('resumeUrl')}
      />

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <label htmlFor="application-note" className="text-sm font-medium text-foreground">
            Anything else you&apos;d like us to know?
          </label>
          <span className="text-xs tabular-nums text-muted-foreground" aria-hidden="true">
            {noteCount}/{NOTE_MAX}
          </span>
        </div>
        <textarea
          id="application-note"
          rows={4}
          maxLength={NOTE_MAX}
          className="w-full rounded border border-input bg-background px-3.5 py-2.5 text-sm text-foreground shadow-sm transition-[color,box-shadow,border-color] duration-fast ease-out-expo placeholder:text-muted-foreground hover:border-foreground/20 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1 focus-visible:ring-offset-background aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/40"
          placeholder="Notice period, salary expectations, portfolio links…"
          aria-invalid={errors.coverNote ? true : undefined}
          aria-describedby={errors.coverNote ? 'application-note-error' : undefined}
          {...register('coverNote')}
        />
        {errors.coverNote?.message && (
          <p id="application-note-error" className="text-xs text-danger" role="alert">
            {errors.coverNote.message}
          </p>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <Checkbox
          label={
            <>
              By submitting this application, you agree to the following{' '}
              <Link to="/privacy" className="text-primary underline-offset-2 hover:underline">
                Privacy Policy
              </Link>{' '}
              &amp;{' '}
              <Link to="/terms" className="text-primary underline-offset-2 hover:underline">
                Terms &amp; Conditions
              </Link>
              .
            </>
          }
          required
          aria-invalid={errors.consent ? true : undefined}
          {...register('consent')}
        />
        {errors.consent?.message && (
          <p className="text-xs text-danger" role="alert">
            {errors.consent.message}
          </p>
        )}
      </div>

    </FormShell>
  );
}

function ApplicationSuccess({ reduce, onReset }: { reduce: boolean; onReset: () => void }) {
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE_OUT_EXPO }}
      className="flex flex-col items-center gap-4 py-10 text-center"
      role="status"
      aria-live="polite"
    >
      <motion.span
        initial={reduce ? false : { scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, type: 'spring', stiffness: 260, damping: 18 }}
        className="flex size-16 items-center justify-center rounded bg-success/15 text-success"
      >
        <CheckCircle2 className="size-9" aria-hidden="true" strokeWidth={2} />
      </motion.span>
      <div className="space-y-1.5">
        <h3 className="font-display text-2xl font-semibold tracking-tight">
          Application submitted
        </h3>
        <p className="mx-auto max-w-md text-muted-foreground">
          Thank you for your interest in joining Resolute Property Solutions. Our HR team will
          reach out if your profile is shortlisted.
        </p>
      </div>
      <Button variant="outline" onClick={onReset}>
        Submit another application
      </Button>
    </motion.div>
  );
}
